import type { MediaSignal, MediaSignalBulletin } from '@tsr/shared-types';
import { humanizeAgo } from './FreshnessIndicator';
import { signalDisplayTitle, signalSourceLabel, signalTime } from './lib/mediaSignalFeed';
import { isUnackedUrgent, type QueueItem } from './anomalyQueue';

/**
 * WALL bottom-ticker content model (#263 — founder feature).
 *
 * A wall-only sliding crawl cycling brief items across the bottom band of the
 * GOC + PEOC walls (the room the PEOC recon caught running a cable-news + TSX
 * ticker — this is its integrated replacement). Two modes:
 *
 *   AMBIENT — the news crawl. Item selection (documented, in priority order):
 *     1. LLM SURGE BULLETINS first (#65, `media_signal_bulletins`): every
 *        bulletin generated in the last {@link TICKER_BULLETIN_WINDOW_H} hours,
 *        newest first, capped at {@link TICKER_MAX_BULLETINS}. Suppressed
 *        bulletins are INCLUDED — suppression means officials are already
 *        reporting (corroboration, not a second alarm), and on an ambient news
 *        crawl that's still the story. Every bulletin item carries the
 *        "AI SUMMARY · N sources" provenance chrome — never dressed as
 *        reporting.
 *     2. WIRE HEADLINES second (`media_signals`): NEWS-leg signals only
 *        (GDELT + RSS — Bluesky posts are chatter, not headlines), which are
 *        lexicon-matched at ingest by the wire substrate; recency-windowed to
 *        {@link TICKER_HEADLINE_WINDOW_H} hours on the signal's display time
 *        (published, else ingested); a real title required (a crawl item IS a
 *        headline); deduped by normalized title (wire echo); newest first,
 *        filling up to {@link TICKER_MAX_ITEMS} total.
 *
 *   TAKEOVER — on ANY unacked urgent alert the crawl flips to a bold
 *     URGENT-RED takeover sliding the alert headline(s) + brief details until
 *     every urgent is acknowledged, then ambient resumes. Multiple unacked
 *     urgents ROTATE (they ride the same sliding loop in queue order). The
 *     urgent predicate IS the queue's standing-alarm rule
 *     (`anomalyQueue.isUnackedUrgent`, shared): Response Level ≥ 3 and
 *     not acknowledged — smoke/media-signal igniter rows are pre-fusion /
 *     unscored and never take the ticker over.
 *
 * NEVER on the COP — the ticker exists only as a wall zone kind (`wallConfig`).
 * Motion posture: the ticker zone joins the media tiles as a designated
 * continuous-motion zone on the wall (PRD §4.26.2); the map motion law
 * (red + live-green only) is about map markers and is unchanged.
 *
 * Pure + deterministic (callers inject `nowMs`) so selection, takeover and
 * rotation are unit-testable; the React zone fetches and feeds it.
 */

/** Bulletins stay on the crawl this long after generation. Tighter than the
 *  feed's 72h default — a wall crawl is "what is happening now". */
export const TICKER_BULLETIN_WINDOW_H = 24;
/** Headline recency window (published, else ingested). */
export const TICKER_HEADLINE_WINDOW_H = 12;
/** Bulletin cap — bulletins lead the crawl but never crowd headlines out. */
export const TICKER_MAX_BULLETINS = 8;
/** Total ambient item cap — bounds one crawl revolution to a readable length. */
export const TICKER_MAX_ITEMS = 24;

export interface TickerItem {
  /** Stable key: `bulletin:<id>` / `headline:<id>`. */
  id: string;
  kind: 'bulletin' | 'headline';
  /** Provenance kicker rendered before the text (uppercase chip), e.g.
   *  "AI SUMMARY · WILDFIRE · 4 SOURCES" or "GDELT · Kelowna BC · 2 hours ago". */
  kicker: string;
  /** The crawl text — a bulletin summary or a wire headline. */
  text: string;
  /** ISO timestamp the item is ordered by (newest first within its tier). */
  timeIso: string;
}

const parseMs = (iso: string | null | undefined): number => {
  const ms = iso ? Date.parse(iso) : Number.NaN;
  return Number.isFinite(ms) ? ms : Number.NaN;
};

/** Normalize a headline for the dedupe key (case/space/punct-insensitive). */
function titleKey(title: string): string {
  return title
    .toLowerCase()
    .replace(/[^\p{L}\p{N}]+/gu, ' ')
    .trim();
}

export interface SelectTickerItemsOpts {
  bulletins: MediaSignalBulletin[];
  signals: MediaSignal[];
  nowMs: number;
  maxItems?: number;
}

/** Build the ambient crawl list — see the module doc for the selection rules. */
export function selectTickerItems(opts: SelectTickerItemsOpts): TickerItem[] {
  const { bulletins, signals, nowMs } = opts;
  const maxItems = opts.maxItems ?? TICKER_MAX_ITEMS;
  const items: TickerItem[] = [];

  // 1) Surge bulletins, newest first, windowed + capped.
  const bulletinFloor = nowMs - TICKER_BULLETIN_WINDOW_H * 3_600_000;
  const freshBulletins = bulletins
    .filter((b) => {
      const ms = parseMs(b.generatedAt);
      return Number.isFinite(ms) && ms >= bulletinFloor && ms <= nowMs + 60_000;
    })
    .sort((a, b) => parseMs(b.generatedAt) - parseMs(a.generatedAt))
    .slice(0, TICKER_MAX_BULLETINS);
  for (const b of freshBulletins) {
    items.push({
      id: `bulletin:${b.id}`,
      kind: 'bulletin',
      kicker: `AI SUMMARY · ${b.hazardKey.replace(/_/g, ' ')} · ${b.sourceCount} ${
        b.sourceCount === 1 ? 'source' : 'sources'
      }`,
      text: b.summary,
      timeIso: b.generatedAt,
    });
  }

  // 2) Wire headlines: news legs only, titled, windowed, title-deduped.
  const headlineFloor = nowMs - TICKER_HEADLINE_WINDOW_H * 3_600_000;
  const seenTitles = new Set<string>();
  const headlines = signals
    .filter((s) => s.source === 'gdelt' || s.source === 'rss')
    .filter((s) => !!s.title?.trim())
    .filter((s) => {
      const ms = parseMs(signalTime(s));
      return Number.isFinite(ms) && ms >= headlineFloor;
    })
    .sort((a, b) => parseMs(signalTime(b)) - parseMs(signalTime(a)));
  for (const s of headlines) {
    if (items.length >= maxItems) break;
    const key = titleKey(s.title!);
    if (!key || seenTitles.has(key)) continue;
    seenTitles.add(key);
    const when = humanizeAgo(Math.max(0, nowMs - parseMs(signalTime(s))));
    const place = s.geoName ? ` · ${s.geoName}` : '';
    items.push({
      id: `headline:${s.id}`,
      kind: 'headline',
      kicker: `${signalSourceLabel(s.source)}${place} · ${when}`,
      text: signalDisplayTitle(s),
      timeIso: signalTime(s),
    });
  }

  return items.slice(0, maxItems);
}

export interface TickerTakeoverItem {
  /** The queue id (`source:uuid`) — stable across refreshes. */
  id: string;
  /** Hazard glyph + title, e.g. "🔥 High-Risk Hotspot — Kenora ON". */
  headline: string;
  /** Brief details: kicker · owner/state · age. */
  detail: string;
}

/** The URGENT-RED takeover list: every unacked urgent (the shared
 *  `anomalyQueue.isUnackedUrgent` predicate — smoke / media-signal igniter
 *  rows are unscored and never take the ticker over), queue-ordered (rank
 *  desc, then recency desc — the group-queue order), each as headline+detail.
 *  Empty array = no takeover, ambient crawl runs. */
export function selectTakeoverItems(queue: QueueItem[], nowMs: number): TickerTakeoverItem[] {
  return queue
    .filter(isUnackedUrgent)
    .sort((a, b) => {
      if (b.rank !== a.rank) return b.rank - a.rank;
      return (parseMs(b.timeIso) || 0) - (parseMs(a.timeIso) || 0);
    })
    .map((it) => {
      const age = Number.isFinite(parseMs(it.timeIso))
        ? humanizeAgo(Math.max(0, nowMs - parseMs(it.timeIso)))
        : null;
      const state = it.owner ? `${it.owner} · ${it.microStatus}` : 'unassigned';
      return {
        id: it.id,
        headline: `${it.hazardIcon} ${it.title}`,
        detail: [it.kicker, state, age].filter(Boolean).join(' · '),
      };
    });
}

/** Crawl speed (px/s) — calm, readable at wall distance. One revolution of the
 *  duplicated track spans half its scroll width, so duration = (w/2)/speed. */
export const TICKER_SPEED_PX_S = 90;

/** Animation duration (seconds) for a measured track width (the DUPLICATED
 *  content — the keyframes translate −50%). Clamped to a sane floor so a
 *  near-empty track never spins comically fast. */
export function tickerDurationS(trackWidthPx: number): number {
  if (!Number.isFinite(trackWidthPx) || trackWidthPx <= 0) return 60;
  return Math.max(20, trackWidthPx / 2 / TICKER_SPEED_PX_S);
}
